import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  username: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  is_superuser: boolean;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginDemo: () => Promise<void>;
  register: (email: string, password: string, fullName: string, username: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  isAuthenticated: boolean;
  isDemoMode: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Configure axios defaults - use environment variable for production, proxy for development
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

// Add request interceptor to include token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`📤 API Request: ${config.method?.toUpperCase()} ${config.url} [with token]`);
    } else {
      console.log(`📤 API Request: ${config.method?.toUpperCase()} ${config.url} [no token]`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    console.log(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url} - Status: ${error.response?.status}`);

    // Skip refresh for auth endpoints to prevent loops
    if (originalRequest.url?.includes('/api/auth/')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const response = await axios.post('/api/auth/refresh', {
          refresh_token: refreshToken,
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', newRefreshToken);
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        
        processQueue(null, access_token);
        isRefreshing = false;
        
        return axios(originalRequest);
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        processQueue(refreshError, null);
        isRefreshing = false;
        
        // Clear tokens on refresh failure - force re-login
        console.log('🧹 Clearing tokens and forcing logout');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        delete axios.defaults.headers.common['Authorization'];
        
        // Redirect to login if on protected page
        if (window.location.pathname.includes('/dashboard') || 
            window.location.pathname.includes('/app') ||
            window.location.pathname.includes('/profile') ||
            window.location.pathname.includes('/admin')) {
          console.log('↩️ Redirecting to login page');
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Fetch current user on mount
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          // Parse user from JWT token instead of API call
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          
          // Check if token is expired
          if (tokenPayload.exp && tokenPayload.exp * 1000 < Date.now()) {
            console.log('Token expired, clearing auth');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setUser(null);
          } else if (!tokenPayload.sub || !tokenPayload.email) {
            // Invalid token without required fields
            console.log('Invalid token payload, clearing auth');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setUser(null);
          } else {
            setUser({
              id: tokenPayload.sub,
              email: tokenPayload.email,
              username: tokenPayload.username,
              full_name: tokenPayload.full_name,
              is_active: true,
              is_admin: tokenPayload.is_admin || false,
              is_superuser: tokenPayload.is_superuser || false,
              created_at: tokenPayload.created_at || new Date().toISOString(),
            });
          }
        } catch (error) {
          console.error('Failed to parse user from token:', error);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setUser(null);
        }
      }
      // ALWAYS set loading to false to prevent infinite loading
      setLoading(false);
    };

    fetchUser();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('🔐 Attempting login for:', email);
      const response = await axios.post('/api/auth/login', {
        email,
        password,
      });

      console.log('✅ Login response received:', response.status);
      const { access_token, refresh_token } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      // Set the authorization header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      console.log('🔑 Authorization header set');

      // Set a basic user object from the JWT token
      const tokenPayload = JSON.parse(atob(access_token.split('.')[1]));
      console.log('👤 Token payload:', tokenPayload);
      
      setUser({
        id: tokenPayload.sub,
        email: tokenPayload.email || email,
        username: tokenPayload.username || email.split('@')[0],
        full_name: tokenPayload.full_name || email.split('@')[0],
        is_active: true,
        is_admin: tokenPayload.is_admin || false,
        is_superuser: tokenPayload.is_superuser || false,
        created_at: tokenPayload.created_at || new Date().toISOString(),
      });
      setIsDemoMode(false);
      console.log('✅ User set successfully');
    } catch (error: any) {
      console.error('❌ Login failed:', error.response?.status, error.response?.data);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }
      
      if (error.response?.status === 422) {
        const details = error.response?.data?.detail;
        if (Array.isArray(details)) {
          throw new Error('Please enter a valid email and password.');
        }
        throw new Error('Please check your email and password format.');
      }
      
      // Don't auto-fallback to demo mode - let user explicitly choose
      if (error.response?.status === 503 || error.response?.status === 404) {
        throw new Error('Authentication service is temporarily unavailable. Please try again later or use demo mode.');
      }
      
      throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  const loginDemo = async () => {
    try {
      const response = await axios.post('/api/auth/demo-login');

      const { access_token, refresh_token } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      // Set a demo user object from the JWT token
      const tokenPayload = JSON.parse(atob(access_token.split('.')[1]));
      setUser({
        id: tokenPayload.sub,
        email: tokenPayload.email || 'demo@quickbuildr.ai',
        username: tokenPayload.username || 'Demo User',
        full_name: tokenPayload.full_name || 'Demo User',
        is_active: true,
        is_admin: tokenPayload.is_admin || false,
        is_superuser: tokenPayload.is_superuser || false,
        created_at: new Date().toISOString(),
      });
      setIsDemoMode(true);
      console.log('Demo login successful');
    } catch (error: any) {
      console.error('Demo login failed:', error);
      throw new Error('Demo mode is not available. Please try again later.');
    }
  };

  const register = async (
    email: string,
    password: string,
    fullName: string,
    username: string
  ) => {
    try {
      // Register the user
      const response = await axios.post('/api/auth/register', {
        email,
        password,
        full_name: fullName,
        username,
      });

      // If registration returns tokens directly, use them
      if (response.data.access_token) {
        const { access_token, refresh_token } = response.data;
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        // Set user from token
        const tokenPayload = JSON.parse(atob(access_token.split('.')[1]));
        setUser({
          id: tokenPayload.sub,
          email: tokenPayload.email || email,
          username: tokenPayload.username || username,
          full_name: tokenPayload.full_name || fullName,
          is_active: true,
          is_admin: tokenPayload.is_admin || false,
          is_superuser: tokenPayload.is_superuser || false,
          created_at: new Date().toISOString(),
        });
      } else if (response.data.approval_status === 'pending') {
        // User registered but needs approval - don't auto-login
        console.log('✅ User registered, pending approval:', response.data.email);
        // Don't call login - just return success
        // Frontend should show the approval_message to user
      } else {
        // Otherwise, auto-login after registration (for approved users)
        await login(email, password);
      }
    } catch (error: any) {
      console.error('Registration failed:', error);
      
      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const details = error.response?.data?.detail;
        if (Array.isArray(details)) {
          // Parse Pydantic validation errors
          const errors = details.map((err: any) => {
            if (err.loc?.includes('username')) {
              return 'Username is required and must be at least 3 characters';
            }
            if (err.loc?.includes('password')) {
              return 'Password must be at least 8 characters';
            }
            if (err.loc?.includes('email')) {
              return 'Please provide a valid email address';
            }
            return err.msg || 'Invalid input';
          });
          throw new Error(errors[0]); // Show first error
        }
        throw new Error('Please check all fields are filled correctly');
      }
      
      // Handle conflict errors (409 - username/email taken)
      if (error.response?.status === 409) {
        throw new Error('Username or email already exists. Please try different credentials.');
      }
      
      // Handle service unavailable
      if (error.response?.status === 404 || error.response?.status === 503) {
        throw new Error('Authentication service is temporarily unavailable. Please try again later.');
      }
      
      // Default error message
      throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsDemoMode(false);
    
    // Call logout endpoint to invalidate token server-side if implemented
    axios.post('/api/auth/logout').catch(() => {
      // Ignore errors, we're logging out anyway
    });
  }, []);

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post('/api/auth/refresh', {
        refresh_token: refreshToken,
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefreshToken);
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    loginDemo,
    register,
    logout,
    refreshToken,
    isAuthenticated: !!user,
    isDemoMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};